import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import CodeIcon from "@mui/icons-material/Code";
import SecurityIcon from "@mui/icons-material/Security";
import CloudIcon from "@mui/icons-material/Cloud";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import "../styles/skills.css";

const skillsData = [
  {
    id: "software",
    roles: ["software"],
    icon: <CodeIcon className="skill-card-icon" />,
    title: "Software Engineering",
    description:
      "Full-stack development across modern web frameworks, backend APIs, and scalable architecture.",
    tags: ["React", "Next.js", "Node.js", "C#", ".NET", "REST APIs", "TypeScript", "Django"],
  },
  {
    id: "cyber",
    roles: ["cyber"],
    icon: <SecurityIcon className="skill-card-icon" />,
    title: "Cyber Security",
    description:
      "Offensive and defensive security — from penetration testing to threat detection and SIEM analysis.",
    tags: ["Pentesting", "OWASP", "SIEM", "OSINT", "Burp Suite", "Nmap", "Wireshark"],
  },
  {
    id: "devops",
    roles: ["devops"],
    icon: <CloudIcon className="skill-card-icon" />,
    title: "DevSecOps",
    description:
      "Automating infrastructure and embedding security throughout the CI/CD pipeline.",
    tags: ["Docker", "Kubernetes", "CI/CD", "GitHub Actions", "Terraform", "Linux"],
  },
];

const stackData = [
  { label: "Languages", pills: ["JavaScript", "TypeScript", "C#", "Python", "Bash"] },
  { label: "Frontend",  pills: ["React", "Next.js", "HTML5", "CSS3", "MUI", "Django"] },
  { label: "Backend",   pills: ["Node.js", ".NET", "Express", "REST"] },

    { label: "Databases",   pills: ["MongoDB", "MySQL", "PostgreSQL"] },

  { label: "Security",  pills: ["Burp Suite", "Nmap", "Metasploit", "Wireshark", "OSINT"] },
  { label: "DevOps",    pills: ["Docker", "Kubernetes", "GitHub Actions", "Terraform", "Linux"] },
  { label: "Cloud",     pills: ["AWS", "Azure", "Vercel", "Firebase"] },
];

const certs = [
  { label: "CompTIA PenTest+ Certfied", icon: <EmojiEventsIcon className="cert-chip-icon" /> },
  { label: "CompTIA CySA+ Certfied",   icon: <EmojiEventsIcon className="cert-chip-icon" /> },
];

const FILTERS = ["all", "software", "cyber", "devops"];

function Skills() {
  const [activeRole, setActiveRole] = useState("all");

  const isVisible = (roles) =>
    activeRole === "all" || roles.includes(activeRole);

  return (
    <div className="skills-page">

      {/* Page-level SEO meta tags */}
      <Helmet>
        <title>Skills | Biko — MERN Stack, Cyber Security & DevSecOps</title>
        <meta
          name="description"
          content="Technical skills of Biko — React, Next.js, Node.js, .NET, Docker, Kubernetes, Pentesting, SIEM, OWASP. CompTIA PenTest+ and CySA+ certified."
        />
        <meta property="og:title" content="Skills | Biko Portfolio" />
        <meta
          property="og:description"
          content="Skills across software engineering, offensive security & Defensive Security, and cloud-native DevSecOps."
        />
        <meta property="og:url" content="https://bikosphere.com/skills" />
      </Helmet>

      <div className="skills-page-container">

        {/* Header */}
        <div className="skills-page-header">
          <h1>My<span>Expertise</span></h1>
          <p>
            A Skill set spanning software engineering,
            offensive security & Defensive Security, and DevSecOps.
          </p>
        </div>

        {/* Filters */}
        <div className="role-filters">
          {FILTERS.map((role) => (
            <button
              key={role}
              className={`filter-btn ${activeRole === role ? "active" : ""}`}
              onClick={() => setActiveRole(role)}
            >
              {role.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Cards grid */}
        <div className="skills-grid">
          {skillsData.map((card) => (
            <div
              key={card.id}
              className={`skill-card ${!isVisible(card.roles) ? "dim" : ""}`}
            >
              <div className="skill-card-header">
                {card.icon}
                <h3>{card.title}</h3>
              </div>
              <p>{card.description}</p>
              <div className="skill-tags">
                {card.tags.map((tag) => (
                  <span key={tag} className="skill-tag">{tag}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Full stack breakdown */}
        <div className="stack-panel">
          <p className="stack-panel-title">Full Stack Breakdown</p>
          <div className="stack-groups">
            {stackData.map((group) => (
              <div className="stack-group" key={group.label}>
                <h4>{group.label}</h4>
                <div className="stack-pills">
                  {group.pills.map((pill) => (
                    <span key={pill} className="stack-pill">{pill}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div className="certs-strip">
          {certs.map((cert) => (
            <div key={cert.label} className="cert-chip">
              {cert.icon}
              {cert.label}
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

export default Skills;